import { Link } from 'react-router-dom';
import { Head as Helmet } from 'vite-react-ssg';
import { SEO } from '@/components/seo/SEO';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Zap, Globe, Code2, Server, CheckCircle2, ArrowRight, BarChart3 } from 'lucide-react';
import { openCalendlyPopup } from '@/lib/calendly';
import { ServiceBackground } from '@/components/3d/ServiceBackground';
import { ServiceSection, SectionHeader, ServiceStatsBar, ServiceSectionNav, type SectionNavItem, ServiceFAQ, ServiceHeroPanel } from '@/components/services';
import { generateSpeakableSchema } from '@/utils/schemaGenerator';

const speakableSchema = generateSpeakableSchema('https://ortemtech.com/services/nextjs-development/');

const navItems: SectionNavItem[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'use-cases', label: 'Use Cases' },
    { id: 'stack', label: 'Stack' },
    { id: 'cost', label: 'Cost' },
    { id: 'faq', label: 'FAQ' },
];

const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': 'https://ortemtech.com/services/nextjs-development/#service',
    name: 'Next.js Development Services',
    serviceType: 'Next.js Development',
    category: 'Software Development',
    provider: {
        '@type': 'Organization',
        name: 'Ortem Technologies',
        url: 'https://ortemtech.com',
        '@id': 'https://ortemtech.com/#organization',
        image: { '@type': 'ImageObject', url: 'https://ortemtech.com/assets/ot-logo-mark.png' },
    },
    description: 'Expert Next.js development services: high-performance web apps, SaaS platforms, and e-commerce solutions built with Next.js 14 App Router, TypeScript, and Tailwind CSS.',
    url: 'https://ortemtech.com/services/nextjs-development/',
    areaServed: { '@type': 'Place', name: 'Worldwide' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://ortemtech.com/services/nextjs-development/' },
};

const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ortemtech.com/' },
        { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://ortemtech.com/services/' },
        { '@type': 'ListItem', position: 3, name: 'Next.js Development', item: 'https://ortemtech.com/services/nextjs-development/' },
    ],
};

const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'Why choose Next.js for a web application?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Next.js 14 with the App Router gives you server-side rendering (SSR), static site generation (SSG), React Server Components, and incremental static regeneration (ISR) in a single framework. This translates to faster page loads, better Core Web Vitals scores, improved SEO, and a better developer experience compared to a plain React SPA.',
            },
        },
        {
            '@type': 'Question',
            name: 'How long does a Next.js project take?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'A Next.js marketing site or landing page takes 2–4 weeks. A SaaS application MVP with authentication, dashboard, and billing takes 8–12 weeks with a team of 2–3 engineers. A large-scale e-commerce or enterprise platform takes 4–8 months depending on complexity and integrations.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you use Next.js 13/14 App Router or Pages Router?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'All new projects use the Next.js 14 App Router with React Server Components. For existing projects using Pages Router, we migrate incrementally — you do not need a full rewrite. We keep up with Next.js releases and use stable features only, avoiding experimental APIs in production.',
            },
        },
        {
            '@type': 'Question',
            name: 'Can you migrate an existing React or Gatsby site to Next.js?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. We migrate existing React SPAs, Gatsby sites, and WordPress front-ends to Next.js. The migration typically involves restructuring routing, converting API calls to Server Components or Route Handlers, and optimising images using next/image. We deliver a migration plan with milestones and a compatibility shim strategy before writing a line of code.',
            },
        },
        {
            '@type': 'Question',
            name: 'How much does a Next.js project cost?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'A marketing site or landing page runs $8,000–$18,000 over 2–4 weeks. A SaaS MVP with authentication, dashboard, and billing runs $35,000–$70,000 over 8–12 weeks. Enterprise or e-commerce platforms with complex integrations start at $90,000 and take 4–8 months. Every quote is fixed-scope after a free discovery call.',
            },
        },
        {
            '@type': 'Question',
            name: 'Should we deploy on Vercel or self-host?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Vercel is the path of least resistance — it\'s built by the Next.js team, so edge caching, ISR, and image optimisation work with zero configuration. We recommend self-hosting on AWS or GCP with Docker when you have existing infrastructure requirements, data residency rules that Vercel\'s regions don\'t satisfy, or cost sensitivity at very high traffic volumes. We configure CI/CD and monitoring either way.',
            },
        },
    ],
};

const features = [
    {
        icon: Zap,
        title: 'Core Web Vitals Optimised',
        description: 'Server Components, streaming SSR, and next/image deliver LCP under 2.5s and CLS under 0.1 — the scores that rank in Google and convert visitors.',
    },
    {
        icon: Globe,
        title: 'SEO-First Architecture',
        description: 'Dynamic metadata, sitemap generation, Open Graph tags, JSON-LD structured data, and canonical URLs — built into every page from day one.',
    },
    {
        icon: Code2,
        title: 'TypeScript + App Router',
        description: 'Full TypeScript codebase with Next.js 14 App Router, React Server Components, and Route Handlers. Modern patterns, maintainable code.',
    },
    {
        icon: Server,
        title: 'API Routes & Server Actions',
        description: 'Backend logic co-located with your frontend using Route Handlers and Server Actions. Single deployment, no separate API server for simpler workloads.',
    },
    {
        icon: BarChart3,
        title: 'E-Commerce & SaaS Ready',
        description: 'Stripe integration, Supabase or PlanetScale for data, Clerk or NextAuth for authentication — the standard Next.js stack for production SaaS and commerce.',
    },
    {
        icon: CheckCircle2,
        title: 'Vercel & Self-Hosted Deploy',
        description: 'Optimised Vercel deployments with edge caching, or self-hosted on AWS/GCP using Docker. We configure CI/CD, preview deployments, and monitoring.',
    },
];

const useCases = [
    'SaaS application frontends and dashboards',
    'B2B marketing and conversion-optimised landing pages',
    'Headless e-commerce storefronts (Shopify, Commerce Layer)',
    'Content-heavy platforms with thousands of pages',
    'Internal tools and admin dashboards',
    'API-integrated product catalogues',
    'Next.js migration from Create React App or Gatsby',
    'Existing codebase performance audit and optimisation',
];

const heroPanelData = {
    title: 'Next.js Development Company',
    services: features.map((f) => ({ title: f.title })),
};

const stack = [
    { label: 'Framework', value: 'Next.js 14 App Router' },
    { label: 'Language', value: 'TypeScript' },
    { label: 'Styling', value: 'Tailwind CSS + shadcn/ui' },
    { label: 'Database', value: 'Supabase, PlanetScale, Neon' },
    { label: 'Auth', value: 'Clerk, NextAuth, Supabase Auth' },
    { label: 'Deploy', value: 'Vercel, AWS, self-hosted Docker' },
];

const NextJSDevelopment = () => {
    return (
        <>
            <SEO page="nextjs-development" />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(speakableSchema)}</script>
            </Helmet>

            <div className="min-h-screen bg-[hsl(var(--svc-ground))]">
                <Header />
                <main>
                    {/* Hero — copy and h1 unchanged; adds ServiceBackground */}
                    <section className="relative overflow-hidden border-b border-[hsl(var(--svc-line))] py-20 sm:py-28">
                        <ServiceBackground serviceType="web" />
                        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
                            <div className="max-w-3xl">
                                <p className="svc-eyebrow mb-5">Web Development</p>
                                <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-[-0.025em] text-[hsl(var(--svc-ink))] text-balance sm:text-5xl lg:text-6xl mb-5">
                                    Next.js Development Company
                                </h1>
                                <p className="mb-5 text-lg font-medium text-[hsl(var(--svc-brand))] sm:text-xl">
                                    Core Web Vitals Optimised, SEO-First & Production-Ready from Day One
                                </p>
                                <p className="mb-8 max-w-[65ch] text-base leading-relaxed text-[hsl(var(--svc-ink-dim))] sm:text-lg">
                                    High-performance web applications and SaaS platforms built with Next.js 14, React Server Components, and TypeScript. Core Web Vitals optimised, SEO-first, and production-ready from day one.
                                </p>
                                <div className="flex flex-col gap-4 sm:flex-row">
                                    <Button size="lg" onClick={() => openCalendlyPopup('https://calendly.com/pravinjha/30min')} className="bg-primary hover:bg-primary/90">
                                        Get a Next.js Quote <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                    <Button size="lg" variant="outline" asChild>
                                        <Link to="/services/web-development/">All Web Services</Link>
                                    </Button>
                                </div>
                            </div>

                            <ServiceHeroPanel
                                service={heroPanelData}
                                className="hidden lg:block"
                                offeringsHref="#services"
                            />
                            </div>
                        </div>
                    </section>

                    <ServiceStatsBar className="lg:hidden" />

                    <ServiceSectionNav items={navItems} />

                    {/* About Our Next.js Practice — prose overview */}
                    <ServiceSection id="overview" tone="raised">
                        <div className="prose prose-lg dark:prose-invert max-w-none">
                            <p>
                                Next.js earned its default-choice status by collapsing what used to be separate decisions — rendering strategy, routing, API layer, image optimisation — into one framework with sane defaults. The App Router and React Server Components mean a page can fetch data on the server, stream it to the client, and ship less JavaScript than an equivalent client-rendered SPA, all without the manual plumbing that used to make SSR a specialist skill.
                            </p>
                            <h2>Why that matters for Core Web Vitals and SEO</h2>
                            <p>
                                Server Components and streaming SSR are the mechanism, but the outcome is what a business actually cares about: faster Largest Contentful Paint, lower Cumulative Layout Shift, and a page that Google's crawler sees fully rendered on the first request — no client-side JavaScript execution required to read your content. That combination is why Next.js sites consistently outperform plain React SPAs on both Core Web Vitals and organic search visibility.
                            </p>
                            <h2>Where React Server Components change the architecture</h2>
                            <p>
                                The practical shift with Server Components is that data fetching moves to the server by default — a product page can query your database directly in the component, no separate API endpoint required for that read. Client Components are still available for anything interactive (forms, dropdowns, real-time widgets), so the architecture question becomes "does this need to run in the browser?" rather than "how do I fetch data for this page?" We draw that line carefully during discovery since getting it wrong either ships too much JavaScript or makes an interactive feature awkward to build.
                            </p>
                            <p>
                                Ready to build with Next.js? <Link to="/contact/" className="hover:underline font-medium">Get a free Next.js proposal →</Link> Tell us about your project; we'll scope the architecture and reply within 48 hours.
                            </p>
                        </div>
                    </ServiceSection>

                    {/* Features */}
                    <ServiceSection id="services">
                        <SectionHeader eyebrow="Why Next.js" title="Why Next.js, and Why Now" />
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {features.map((feature, i) => (
                                <div key={i} className="svc-card svc-card-interactive p-6">
                                    <feature.icon className="mb-4 h-6 w-6 text-[hsl(var(--svc-signal))]" aria-hidden="true" />
                                    <h3 className="mb-2 font-semibold text-[hsl(var(--svc-ink))]">{feature.title}</h3>
                                    <p className="text-sm leading-relaxed text-[hsl(var(--svc-ink-dim))]">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </ServiceSection>

                    {/* Use Cases */}
                    <ServiceSection id="use-cases" tone="raised">
                        <SectionHeader eyebrow="What we build" title="What We Build with Next.js" />
                        <ul className="grid max-w-4xl grid-cols-1 gap-x-10 sm:grid-cols-2">
                            {useCases.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 border-b border-[hsl(var(--svc-line))] py-3.5">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--svc-signal))]" aria-hidden="true" />
                                    <span className="text-sm text-[hsl(var(--svc-ink-dim))]">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </ServiceSection>

                    {/* Stack */}
                    <ServiceSection id="stack">
                        <SectionHeader eyebrow="Stack" title="Our Next.js Stack" />
                        <div className="grid max-w-4xl grid-cols-1 gap-px overflow-hidden rounded-[4px] border border-[hsl(var(--svc-line))] bg-[hsl(var(--svc-line))] sm:grid-cols-2 lg:grid-cols-3">
                            {stack.map((item, i) => (
                                <div key={i} className="bg-[hsl(var(--svc-surface))] p-4">
                                    <p className="svc-mono mb-1 text-[0.625rem] uppercase tracking-[0.12em] text-[hsl(var(--svc-ink-faint))]">{item.label}</p>
                                    <p className="text-sm text-[hsl(var(--svc-ink))]">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    </ServiceSection>

                    {/* Cost & Timeline */}
                    <ServiceSection id="cost">
                        <SectionHeader eyebrow="Investment" title="Next.js Project Cost & Timeline" />
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[560px] border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-[hsl(var(--svc-line))] text-left">
                                        <th className="py-3 pr-4 font-semibold text-[hsl(var(--svc-ink))]">Scope</th>
                                        <th className="py-3 pr-4 font-semibold text-[hsl(var(--svc-ink))]">Typical Cost</th>
                                        <th className="py-3 font-semibold text-[hsl(var(--svc-ink))]">Timeline</th>
                                    </tr>
                                </thead>
                                <tbody className="text-[hsl(var(--svc-ink-dim))]">
                                    <tr className="border-b border-[hsl(var(--svc-line))]">
                                        <td className="py-3 pr-4">Marketing site / landing page</td>
                                        <td className="py-3 pr-4">$8,000 – $18,000</td>
                                        <td className="py-3">2–4 weeks</td>
                                    </tr>
                                    <tr className="border-b border-[hsl(var(--svc-line))]">
                                        <td className="py-3 pr-4">SaaS MVP (auth, dashboard, billing)</td>
                                        <td className="py-3 pr-4">$35,000 – $70,000</td>
                                        <td className="py-3">8–12 weeks</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 pr-4">Enterprise / e-commerce platform</td>
                                        <td className="py-3 pr-4">$90,000+</td>
                                        <td className="py-3">4–8 months</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-4 text-sm text-[hsl(var(--svc-ink-dim))]">
                            Ranges reflect fixed-scope quotes after a discovery call — not hourly billing. Every quote is itemised by feature.
                        </p>
                    </ServiceSection>

                    {/* FAQ */}
                    <ServiceFAQ
                        heading="Next.js Development FAQs"
                        faqs={faqSchema.mainEntity.map(q => ({ question: q.name, answer: q.acceptedAnswer.text }))}
                    />

                    {/* CTA */}
                    <ServiceSection>
                        <div className="svc-card relative overflow-hidden p-8 text-center sm:p-12">
                            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[2px] bg-[hsl(var(--svc-signal))]" />
                            <div className="mx-auto max-w-2xl">
                                <h2 className="mb-4 font-display text-2xl font-bold leading-[1.15] tracking-[-0.02em] text-[hsl(var(--svc-ink))] text-balance sm:text-3xl">
                                    Ready to Build with Next.js?
                                </h2>
                                <p className="mb-8 text-[hsl(var(--svc-ink-dim))]">
                                    Tell us about your project. We'll scope the architecture, estimate the timeline, and give you a transparent fixed-price proposal within 48 hours.
                                </p>
                                <Button size="lg" onClick={() => openCalendlyPopup('https://calendly.com/pravinjha/30min')} className="mb-8 bg-primary hover:bg-primary/90">
                                    Get a Free Next.js Proposal <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <p className="text-sm text-[hsl(var(--svc-ink-dim))]">
                                    Also see:{' '}
                                    <Link to="/services/web-development/" className="hover:underline">Web Development</Link>
                                    {' · '}
                                    <Link to="/services/custom-app-development/" className="hover:underline">Custom App Development</Link>
                                    {' · '}
                                    <Link to="/portfolio/" className="hover:underline">Our Portfolio</Link>
                                </p>
                            </div>
                        </div>
                    </ServiceSection>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default NextJSDevelopment;
